/**
 * VoiceInputButton - Microphone button for speech-to-text input
 *
 * Place next to any text input/textarea. When clicked, starts listening
 * and appends the spoken text to the input value.
 *
 * Uses the Web Speech Recognition API (free, built into Chrome/Edge/Safari).
 *
 * Usage:
 *   <div style={{ display: 'flex', gap: 8 }}>
 *     <input value={value} onChange={e => setValue(e.target.value)} />
 *     <VoiceInputButton onResult={text => setValue(prev => prev + ' ' + text)} />
 *   </div>
 */

import React, { useState, useRef } from 'react';
import { AccessibilityService } from '../services/AccessibilityService';

interface VoiceInputButtonProps {
  onResult: (text: string) => void;
  language?: string;
  size?: number;
  style?: React.CSSProperties;
}

export default function VoiceInputButton({
  onResult,
  language = 'en-US',
  size = 20,
  style,
}: VoiceInputButtonProps) {
  const [isListening, setIsListening] = useState(false);
  const stopRef = useRef<(() => void) | null>(null);

  if (!AccessibilityService.isSpeechRecognitionSupported()) {
    return null; // Hide button if browser doesn't support speech recognition
  }

  const handleClick = () => {
    if (isListening && stopRef.current) {
      stopRef.current();
      stopRef.current = null;
      setIsListening(false);
      return;
    }

    setIsListening(true);
    stopRef.current = AccessibilityService.startSpeechRecognition(
      (text) => {
        onResult(text);
      },
      () => {
        setIsListening(false);
        stopRef.current = null;
      },
      language
    );
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isListening ? 'Stop listening' : 'Speak to type'}
      title={isListening ? 'Listening... click to stop' : 'Click to speak'}
      style={{
        width: size + 12,
        height: size + 12,
        borderRadius: '50%',
        border: isListening ? '2px solid #ef4444' : '1px solid #cbd5e1',
        backgroundColor: isListening ? 'rgba(239, 68, 68, 0.1)' : 'rgba(148, 163, 184, 0.1)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s',
        flexShrink: 0,
        ...style,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={isListening ? '#ef4444' : '#94a3b8'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
      {isListening && (
        <span
          style={{
            position: 'absolute',
            width: size + 12,
            height: size + 12,
            borderRadius: '50%',
            border: '2px solid #ef4444',
            animation: 'voicePulse 1.5s ease-in-out infinite',
            pointerEvents: 'none',
          }}
        />
      )}
    </button>
  );
}
